import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";
import { AiResponseNodeData } from "pages/FlowBuilderv2/Nodes/NodeData";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import AiPromptEditor, { AiPromptEditorHandle } from "./AiPromptEditor";
import { startPlaceholderDrag } from "./placeholderDrag";
import { PLACEHOLDER_FIELDS, PlaceholdersResponse } from "./placeholderFields";
import {
  AI_RESPONSE_TEMPLATES,
  AiResponseTemplate,
} from "./aiResponseTemplates";
import Modal from "components/Elements/Modal";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import { useQueryClient } from "@tanstack/react-query";
import {
  flowAiAgentsQuery,
  flowChannelsQuery,
  flowPlaceholdersQuery,
} from "pages/FlowBuilderv2/flowBuilderQueries";
import { AiAgent, AiAgentType } from "types/AiAgent";

const channels: { key: "email" | "slack" | "sms"; label: string }[] = [
  { key: "email", label: "Email" },
  { key: "slack", label: "Slack" },
  { key: "sms", label: "SMS" },
];

const aiAgentTypeOf = (agent: AiAgent): AiAgentType =>
  agent.agentType || (agent.systemKey ? "inbound" : "master");

interface ConnectionOption {
  id: string;
  label: string;
}

interface WorkspaceChannelsResponse {
  mailgunConnections?: { id: string; name?: string; sendingDomain?: string }[];
  sendgridConnections?: { id: string; name?: string }[];
  resendConnections?: { id: string; name?: string }[];
  gmailConnections?: { id: string; email?: string; name?: string }[];
  office365Connections?: { id: string; email?: string; name?: string }[];
  slackInstallations?: {
    id: string;
    installation?: {
      team?: { name?: string };
      enterprise?: { name?: string };
    };
  }[];
  twilioConnections?: { id: string; name?: string }[];
  telnyxConnections?: { id: string; name?: string }[];
}

const AiResponseSettings: FC<SidePanelComponentProps<AiResponseNodeData>> = ({
  nodeData,
  setNodeData,
  setIsError,
  showErrors,
}) => {
  const readOnly = useSidePanelReadOnly();
  const promptInvalid = false;
  const channelInvalid = !nodeData.channel;
  const connectionInvalid = !nodeData.connectionId;

  useEffect(() => {
    setIsError(promptInvalid || channelInvalid || connectionInvalid);
  }, [promptInvalid, channelInvalid, connectionInvalid, setIsError]);

  const update = (patch: Partial<AiResponseNodeData>) =>
    setNodeData({ ...nodeData, ...patch });

  // The template the current prompt still matches verbatim, if any. Drives the
  // dropdown's displayed value so a freshly picked template shows its name.
  const currentTemplateKey =
    AI_RESPONSE_TEMPLATES.find((t) => t.prompt === nodeData.prompt)?.key ?? "";

  // The prompt counts as "edited" once it has text that doesn't exactly match
  // one of the built-in templates, i.e. the user has written or changed it.
  const promptEdited = !!nodeData.prompt?.trim() && !currentTemplateKey;

  const applyTemplate = (tpl: AiResponseTemplate) =>
    update({ prompt: tpl.prompt, maxTurns: tpl.maxTurns });

  // When the user picks a template after editing the prompt, confirm before
  // discarding their edits. The chosen template waits here until confirmed.
  const [pendingTemplate, setPendingTemplate] =
    useState<AiResponseTemplate | null>(null);

  const queryClient = useQueryClient();

  const [channelsData, setChannelsData] = useState<WorkspaceChannelsResponse>(
    {}
  );
  const [agentsData, setAgentsData] = useState<AiAgent[]>([]);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const data = (await queryClient.ensureQueryData(
          flowChannelsQuery()
        )) as WorkspaceChannelsResponse;
        setChannelsData(data || {});
      } catch {
        setChannelsData({});
      }
    })();
  }, [queryClient]);

  useEffect(() => {
    (async () => {
      try {
        const data = (await queryClient.ensureQueryData(
          flowAiAgentsQuery()
        )) as AiAgent[];
        setAgentsData(data || []);
      } catch {
        setAgentsData([]);
      } finally {
        setAgentsLoaded(true);
      }
    })();
  }, [queryClient]);

  useEffect(() => {
    const refreshAgents = async () => {
      setAgentsLoaded(false);
      try {
        const query = flowAiAgentsQuery();
        await queryClient.invalidateQueries({ queryKey: query.queryKey });
        const data = (await queryClient.fetchQuery(query)) as AiAgent[];
        setAgentsData(data || []);
      } catch {
        setAgentsData([]);
      } finally {
        setAgentsLoaded(true);
      }
    };
    window.addEventListener("leadstats:ai-agents-changed", refreshAgents);
    return () =>
      window.removeEventListener("leadstats:ai-agents-changed", refreshAgents);
  }, [queryClient]);

  const connectionOptions: ConnectionOption[] = useMemo(() => {
    if (nodeData.channel === "email") {
      return [
        ...(channelsData.gmailConnections || []).map((c) => ({
          id: c.id,
          label: `Gmail: ${c.email || c.name || c.id}`,
        })),
        ...(channelsData.office365Connections || []).map((c) => ({
          id: c.id,
          label: `Office 365: ${c.email || c.name || c.id}`,
        })),
        ...(channelsData.mailgunConnections || []).map((c) => ({
          id: c.id,
          label: `Mailgun: ${c.sendingDomain || c.name || c.id}`,
        })),
        ...(channelsData.sendgridConnections || []).map((c) => ({
          id: c.id,
          label: `SendGrid: ${c.name || c.id}`,
        })),
        ...(channelsData.resendConnections || []).map((c) => ({
          id: c.id,
          label: `Resend: ${c.name || c.id}`,
        })),
      ];
    }
    if (nodeData.channel === "slack") {
      return (channelsData.slackInstallations || []).map((s) => ({
        id: s.id,
        label:
          s.installation?.team?.name ||
          s.installation?.enterprise?.name ||
          s.id,
      }));
    }
    if (nodeData.channel === "sms") {
      const twilio = (channelsData.twilioConnections || []).map((c) => ({
        id: c.id,
        label: `Twilio: ${c.name || c.id}`,
      }));
      const telnyx = (channelsData.telnyxConnections || []).map((c) => ({
        id: c.id,
        label: `Telnyx: ${c.name || c.id}`,
      }));
      return [...twilio, ...telnyx];
    }
    return [];
  }, [channelsData, nodeData.channel]);

  // Keep the connection in sync with the channel: clear it if it no longer
  // belongs to the chosen channel, and default it when nothing is picked. On
  // email prefer the user's Gmail account; otherwise fall back to the only
  // option when there's just one.
  useEffect(() => {
    // Keep a still-valid choice as-is.
    if (
      nodeData.connectionId &&
      connectionOptions.find((o) => o.id === nodeData.connectionId)
    )
      return;
    // Otherwise pick a default: prefer Gmail on email, else the only option.
    const gmailIds = new Set(
      (channelsData.gmailConnections || []).map((c) => c.id)
    );
    const pick =
      (nodeData.channel === "email" &&
        connectionOptions.find((o) => gmailIds.has(o.id))) ||
      (connectionOptions.length === 1 ? connectionOptions[0] : undefined);
    const nextId = pick ? pick.id : undefined;
    // This both selects the default and clears a now-invalid prior choice.
    if (nextId !== nodeData.connectionId) update({ connectionId: nextId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData.channel, connectionOptions.length]);

  const [activeTab, setActiveTab] = useState<"prompt" | "settings">("prompt");
  const settingsHasError = channelInvalid || connectionInvalid;

  // Saved placeholders the user can drop into the prompt.
  const [placeholders, setPlaceholders] = useState<PlaceholdersResponse>({});
  useEffect(() => {
    (async () => {
      try {
        const data = (await queryClient.ensureQueryData(
          flowPlaceholdersQuery()
        )) as PlaceholdersResponse;
        setPlaceholders(data || {});
      } catch {
        setPlaceholders({});
      }
    })();
  }, [queryClient]);

  // Placeholders that actually have a saved value, the only ones offerable.
  const availablePlaceholders = useMemo(
    () =>
      PLACEHOLDER_FIELDS.filter(
        (f) => (placeholders[f.tag] ?? "").toString().trim().length > 0
      ),
    [placeholders]
  );

  // tag -> saved value, for rendering chips in the prompt editor.
  const placeholderValues = useMemo(() => {
    const map: Record<string, string> = {};
    PLACEHOLDER_FIELDS.forEach((f) => {
      const v = (placeholders[f.tag] ?? "").toString();
      if (v.trim()) map[f.tag] = v;
    });
    return map;
  }, [placeholders]);

  const editorRef = useRef<AiPromptEditorHandle>(null);
  const [placeholderMenuOpen, setPlaceholderMenuOpen] = useState(false);
  const placeholderMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!placeholderMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        placeholderMenuRef.current &&
        !placeholderMenuRef.current.contains(e.target as Node)
      )
        setPlaceholderMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [placeholderMenuOpen]);

  const channelLabel =
    channels.find((c) => c.key === nodeData.channel)?.label || "this channel";
  const defaultAgent = agentsData.find(
    (agent) =>
      agent.isDefault && !agent.isArchived && aiAgentTypeOf(agent) === "master"
  );
  const activeAgents = agentsData.filter(
    (agent) => !agent.isArchived && aiAgentTypeOf(agent) === "master"
  );
  const selectedAgent = nodeData.agentId
    ? agentsData.find((agent) => agent.id === nodeData.agentId)
    : undefined;
  const selectedAgentMissing =
    agentsLoaded && !!nodeData.agentId && !selectedAgent;
  const selectedAgentArchived = !!selectedAgent?.isArchived;

  return (
    <>
      <div
        className={`p-5 flex flex-col gap-5 h-full${
          readOnly ? " pointer-events-none select-none" : ""
        }`}
      >
        <div
          className={`flex gap-2 border-b border-themed-borderSecondary${
            readOnly ? " pointer-events-auto" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => setActiveTab("prompt")}
            className={`px-3 py-2 text-[13px] border-b-2 -mb-px ${
              activeTab === "prompt"
                ? "border-[#7C3AED] text-[#7C3AED] font-semibold"
                : "border-transparent text-themed-text hover:text-[#7C3AED]"
            }`}
          >
            Prompt
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("settings")}
            className={`px-3 py-2 text-[13px] border-b-2 -mb-px flex items-center gap-1 ${
              activeTab === "settings"
                ? "border-[#7C3AED] text-[#7C3AED] font-semibold"
                : "border-transparent text-themed-text hover:text-[#7C3AED]"
            }`}
          >
            Settings
            {showErrors && settingsHasError && (
              <span className="w-[6px] h-[6px] rounded-full bg-[#F43F5E]" />
            )}
          </button>
        </div>

        {activeTab === "prompt" && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex justify-between items-center mb-5 font-inter font-normal text-[14px] leading-[22px]">
              <div>Template</div>
              <div className="flex flex-col gap-[10px]">
                <select
                  value={currentTemplateKey}
                  onChange={(e) => {
                    const tpl = AI_RESPONSE_TEMPLATES.find(
                      (t) => t.key === e.target.value
                    );
                    if (!tpl) return;
                    if (promptEdited) setPendingTemplate(tpl);
                    else applyTemplate(tpl);
                  }}
                  className="w-[200px] h-[32px] rounded-sm px-[12px] py-[4px] text-[14px] font-roboto leading-[22px]"
                >
                  <option value="" disabled hidden>
                    Select a template
                  </option>
                  {AI_RESPONSE_TEMPLATES.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[14px] font-semibold">
                Prompt for this node
              </div>
              <div className="relative" ref={placeholderMenuRef}>
                <button
                  type="button"
                  onClick={() => setPlaceholderMenuOpen((o) => !o)}
                  className="flex items-center gap-1 px-2 py-1 rounded text-[12px] border border-themed-borderSecondary bg-themed-card text-themed-text hover:border-[#7C3AED] hover:text-[#7C3AED]"
                >
                  Placeholders
                  <span className="text-[16px] leading-none">▾</span>
                </button>
                {placeholderMenuOpen && (
                  <div className="absolute right-0 mt-1 z-10 w-[260px] max-h-[260px] overflow-y-auto bg-themed-card border border-themed-borderSecondary rounded shadow-lg py-1">
                    {availablePlaceholders.length === 0 ? (
                      <div className="px-3 py-2 text-[12px] text-themed-textSecondary leading-[18px]">
                        You haven&apos;t set up any placeholder values yet. Add
                        them on{" "}
                        <span className="font-medium">
                          Settings &rarr; Placeholders
                        </span>{" "}
                        to use them here.
                      </div>
                    ) : (
                      availablePlaceholders.map((p) => (
                        <button
                          key={p.tag}
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("text/placeholder", p.tag);
                            e.dataTransfer.effectAllowed = "move";
                            // Hide the native drag image and draw our own pill
                            // following the cursor (just the pill, nothing else).
                            startPlaceholderDrag(
                              e,
                              placeholderValues[p.tag] || ""
                            );
                            // Defer closing so the drag is underway before this
                            // element unmounts; removing it synchronously cancels
                            // the drag.
                            setTimeout(() => setPlaceholderMenuOpen(false), 0);
                          }}
                          onClick={() => {
                            editorRef.current?.insertPlaceholder(p.tag);
                            setPlaceholderMenuOpen(false);
                          }}
                          className="w-full text-left px-3 py-1.5 hover:bg-themed-background flex items-center gap-2 cursor-grab"
                        >
                          <span
                            className="inline-block align-baseline rounded px-1 text-[13px] truncate"
                            style={{
                              color: "var(--foreground)",
                              backgroundColor:
                                "color-mix(in srgb, var(--muted) 50%, transparent)",
                            }}
                          >
                            {placeholderValues[p.tag]}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <AiPromptEditor
              ref={editorRef}
              value={nodeData.prompt || ""}
              onChange={(next) => update({ prompt: next })}
              placeholderValues={placeholderValues}
              placeholder="Describe how the AI should behave: the goal of the conversation, tone, what to ask, when to wrap up."
              className="flex-1 min-h-0"
            />
          </div>
        )}

        {activeTab === "settings" && (
          <>
            <div>
              <div className="text-[14px] font-semibold mb-1">Agent</div>
              <select
                value={nodeData.agentId || ""}
                onChange={(e) =>
                  update({ agentId: e.target.value || undefined })
                }
                className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED] bg-themed-card"
              >
                <option value="">
                  Workspace default
                  {defaultAgent ? ` (${defaultAgent.name})` : ""}
                </option>
                {activeAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
                {selectedAgentArchived && selectedAgent && (
                  <option value={selectedAgent.id}>
                    {selectedAgent.name} (archived)
                  </option>
                )}
                {selectedAgentMissing && (
                  <option value={nodeData.agentId}>Missing agent</option>
                )}
              </select>
              {selectedAgentArchived && (
                <div className="text-[12px] text-[#F59E0B] mt-1">
                  This workflow references an archived agent.
                </div>
              )}
              {selectedAgentMissing && (
                <div className="text-[12px] text-[#F43F5E] mt-1">
                  This workflow references an agent that no longer exists.
                </div>
              )}
            </div>

            <div>
              <div className="text-[14px] font-semibold mb-1">Channel</div>
              <div className="flex gap-2">
                {channels.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => update({ channel: c.key })}
                    className={`px-3 py-2 rounded text-[13px] border ${
                      nodeData.channel === c.key
                        ? "bg-[#7C3AED] text-themed-background border-[#7C3AED]"
                        : "bg-themed-card text-themed-text border-themed-borderSecondary hover:bg-themed-background"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {showErrors && channelInvalid && (
                <div className="text-[12px] text-[#F43F5E] mt-1">
                  Pick a channel
                </div>
              )}
            </div>

            <div>
              <div className="text-[14px] font-semibold mb-1">Connection</div>
              {connectionOptions.length === 0 ? (
                <div className="text-[12px] text-themed-textSecondary border border-themed-border bg-themed-background rounded px-3 py-2">
                  No {channelLabel} connection available. Connect one in{" "}
                  <span className="font-medium">Settings &rarr; Channels</span>{" "}
                  first, then come back here.
                </div>
              ) : (
                <select
                  value={nodeData.connectionId || ""}
                  onChange={(e) => update({ connectionId: e.target.value })}
                  className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED] bg-themed-card"
                >
                  <option value="">Select a connection</option>
                  {connectionOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {showErrors &&
                connectionInvalid &&
                connectionOptions.length > 0 && (
                  <div className="text-[12px] text-[#F43F5E] mt-1">
                    Pick a connection
                  </div>
                )}
            </div>

            {nodeData.channel === "email" && (
              <div>
                <div className="text-[14px] font-semibold mb-1">
                  Email subject
                </div>
                <input
                  type="text"
                  value={nodeData.subject || ""}
                  onChange={(e) => update({ subject: e.target.value })}
                  placeholder="Subject line of the first email"
                  className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
            )}

            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={
                  nodeData.waitForCustomerReplyBeforeFirstResponse === true ||
                  nodeData.replyOnly === true
                }
                onChange={(e) =>
                  update({
                    waitForCustomerReplyBeforeFirstResponse: e.target.checked,
                    replyOnly: e.target.checked,
                  })
                }
                className="mt-1"
              />
              <div>
                <div className="text-[14px] font-semibold">
                  Wait for the customer before the first AI reply
                </div>
                <div className="text-[12px] text-themed-textSecondary">
                  Park at this node without sending an opening AI message. The
                  AI responds after a new customer message arrives.
                </div>
              </div>
            </label>

            <div className="flex gap-3">
              <div className="flex-1">
                <div className="text-[14px] font-semibold mb-1">Max turns</div>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={nodeData.maxTurns ?? 10}
                  onChange={(e) =>
                    update({ maxTurns: parseInt(e.target.value || "10", 10) })
                  }
                  className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold mb-1">
                  Reply timeout (min)
                </div>
                <input
                  type="number"
                  min={1}
                  value={nodeData.replyTimeoutMinutes ?? 1440}
                  onChange={(e) =>
                    update({
                      replyTimeoutMinutes: parseInt(
                        e.target.value || "1440",
                        10
                      ),
                    })
                  }
                  className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED]"
                />
              </div>
            </div>

            <div>
              <div className="text-[14px] font-semibold mb-1">
                When complete
              </div>
              <select
                value={nodeData.onComplete || "advance"}
                onChange={(e) =>
                  update({
                    onComplete: e.target.value as "advance" | "exit",
                  })
                }
                className="w-full px-3 py-2 border border-themed-borderSecondary rounded text-[13px] focus:outline-none focus:border-[#7C3AED]"
              >
                <option value="advance">Advance to next step</option>
                <option value="exit">Exit the workflow</option>
              </select>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={!!pendingTemplate}
        onClose={() => setPendingTemplate(null)}
        closeButtonNeed={false}
      >
        <div>
          <div className="font-medium text-[16px] text-themed-text mb-2">
            Replace your edited prompt?
          </div>
          <div className="text-[14px] text-themed-text leading-[22px] mb-6">
            You've edited this step's prompt. Switching templates will replace
            it with the template's prompt and discard your changes.
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type={ButtonType.SECONDARY}
              onClick={() => setPendingTemplate(null)}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.PRIMARY}
              onClick={() => {
                if (pendingTemplate) applyTemplate(pendingTemplate);
                setPendingTemplate(null);
              }}
            >
              Switch template
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AiResponseSettings;
